import React, { useState, useCallback } from "react";
import { X, AlertTriangle } from "lucide-react";

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  danger = false,
  options = null, // optional: [{ key, label, default?: bool, help?: string }]
  onConfirm,
  onCancel,
}) {
  const [opts, setOpts] = useState(() => {
    if (!Array.isArray(options)) return {};
    const initial = {};
    for (const o of options) initial[o.key] = !!o.default;
    return initial;
  });

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-md w-full shadow-[8px_8px_0_0_#000]" data-testid="confirm-modal">
        <div className="flex items-start gap-3 mb-4">
          {danger && <AlertTriangle className="shrink-0 mt-0.5" strokeWidth={2.5} size={20} />}
          <div className="flex-1">
            <div className="label mb-1">Confirmation</div>
            <h3 className="font-serif text-2xl leading-tight">{title}</h3>
          </div>
          <button onClick={onCancel} className="text-[var(--muted)] hover:text-black" data-testid="confirm-close-btn">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-[var(--text)] mb-4 leading-relaxed">{message}</p>

        {Array.isArray(options) && options.length > 0 && (
          <div className="mb-5 space-y-2 rounded-xl p-3" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
            {options.map((o) => (
              <label key={o.key} className="flex items-start gap-2 cursor-pointer" data-testid={`confirm-option-${o.key}`}>
                <input
                  type="checkbox"
                  className="mt-0.5 w-3.5 h-3.5"
                  checked={!!opts[o.key]}
                  onChange={(e) => setOpts((s) => ({ ...s, [o.key]: e.target.checked }))}
                  data-testid={`confirm-option-input-${o.key}`}
                />
                <span className="text-sm leading-snug">
                  <span className="font-semibold">{o.label}</span>
                  {o.help && (
                    <span className="block text-xs text-[var(--muted)] mt-0.5">{o.help}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="btn-secondary" data-testid="confirm-cancel-btn">
            {cancelLabel}
          </button>
          <button
            onClick={() => onConfirm(opts)}
            className={`btn-primary ${danger ? "bg-black" : ""}`}
            data-testid="confirm-confirm-btn"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm() {
  const [state, setState] = useState({ open: false, _seq: 0 });
  const ask = useCallback(
    (opts) =>
      new Promise((resolve) => {
        const hasOptions = Array.isArray(opts?.options) && opts.options.length > 0;
        setState((prev) => ({
          open: true,
          ...opts,
          _seq: (prev?._seq || 0) + 1,
          onConfirm: (optionValues) => {
            setState((s) => ({ ...s, open: false }));
            // Backward-compatible: true when no options; object when options provided
            resolve(hasOptions ? { confirmed: true, ...(optionValues || {}) } : true);
          },
          onCancel: () => {
            setState((s) => ({ ...s, open: false }));
            resolve(false);
          },
        }));
      }),
    []
  );
  // key forces a fresh ConfirmModal instance per ask, so lazy useState picks up new defaults
  const modal = <ConfirmModal key={state._seq} {...state} />;
  return { ask, modal };
}
